import type { ComparableAnalysisSubjectInput } from "@/graphql"
import { AssetType, LandCategory, LandSubcategory, TransactionParkingType } from "@/graphql"
import {
	TransactionCPTAQ,
	TransactionCodificationType,
	TransactionContamination,
	TransactionCurrentState,
	TransactionInfrastructure,
	TransactionLandShape,
	TransactionPartyType,
	TransactionProjectApproval,
	TransactionProjectApprovalAtDeedOfSale,
	TransactionSaleContext,
	TransactionUrbanPerimeter,
} from "@/model/DataModel"

export type ComparableAnalysisField = {
	key: keyof ComparableAnalysisSubjectInput
	label: string
	type: "text" | "textarea" | "number" | "date" | "checkbox" | "select"
	multiple?: boolean
	options?: string[]
	sensitive?: boolean
	area?: boolean
}

export const comparableAnalysisFields: ComparableAnalysisField[] = [
	{ key: "address", label: "transaction.card.fields.address", type: "text" },
	{ key: "municipalityName", label: "transaction.card.fields.municipality", type: "text" },
	{ key: "postalCode", label: "transaction.card.fields.postalCode", type: "text" },
	{ key: "buyerName", label: "transactions-table.columns.buyer", type: "text" },
	{ key: "buyerNeq", label: "transaction.card.fields.neq", type: "text" },
	{ key: "buyerActivity", label: "transaction.card.fields.activity", type: "text" },
	{ key: "sellerName", label: "transactions-table.columns.seller", type: "text" },
	{ key: "sellerNeq", label: "transaction.card.fields.neq", type: "text" },
	{ key: "sellerActivity", label: "transaction.card.fields.activity", type: "text" },
	{ key: "broker", label: "transaction.card.fields.broker", type: "text", sensitive: true },
	{ key: "agency", label: "transaction.card.fields.agency", type: "text", sensitive: true },
	{ key: "marketplace", label: "transaction.card.fields.marketplace", type: "text", sensitive: true },
	{ key: "marketplaceId", label: "transaction.card.fields.marketplaceId", type: "text", sensitive: true },
	{ key: "lender", label: "transaction.card.fields.lenderName", type: "text", sensitive: true },
	{ key: "projectLink", label: "transaction.card.fields.link", type: "text" },
	{ key: "projectDocument", label: "transaction.card.fields.document", type: "text" },
	{ key: "parcelReport", label: "transaction.card.fields.parcelReport", type: "text" },
	{ key: "deductionComments", label: "transaction.card.fields.deductionComments", type: "textarea" },
	{
		key: "infrastructureDeductionComments",
		label: "transaction.card.fields.infrastructureDeductionComments",
		type: "textarea",
	},
	{ key: "comments", label: "transaction.card.fields.comments", type: "textarea" },
	{ key: "projectComments", label: "transaction.card.fields.projectComments", type: "textarea", sensitive: true },
	{ key: "salePrice", label: "transaction.card.fields.salePrice", type: "number" },
	{ key: "lenderAmount", label: "transaction.card.fields.lenderAmount", type: "number", sensitive: true },
	{ key: "buildableAreaSqft", label: "transaction.card.fields.buildableArea", type: "number", area: true },
	{ key: "netLandAreaSqft", label: "transaction.card.fields.netLandArea", type: "number", area: true },
	{ key: "contaminationCost", label: "transaction.card.fields.contaminationCost", type: "number" },
	{ key: "stories", label: "transaction.card.fields.stories", type: "number" },
	{ key: "numberOfUnits", label: "transaction.card.fields.unitsTotal", type: "number" },
	{ key: "parkingIndoorCount", label: "transaction.card.fields.parkingIndoor", type: "number" },
	{ key: "parkingOutdoorCount", label: "transaction.card.fields.parkingOutdoor", type: "number" },
	{ key: "siteCoverageRatio", label: "transaction.card.fields.siteCoverageRatio", type: "number" },
	{ key: "deductionRatio", label: "transaction.card.fields.deduction", type: "number" },
	{ key: "infrastructureDeductionRatio", label: "transaction.card.fields.infrastructureDeduction", type: "number" },
	{ key: "isGrossLand", label: "transaction.card.fields.isGrossLand", type: "checkbox" },
	{ key: "tod", label: "transaction.card.fields.tod", type: "checkbox" },
	{ key: "mortgage", label: "transaction.card.fields.mortgage", type: "checkbox", sensitive: true },
	{ key: "salesBalance", label: "transaction.card.fields.salesBalance", type: "checkbox", sensitive: true },
	{ key: "wetlands", label: "transaction.card.fields.wetlands", type: "checkbox" },
	{ key: "floodableZone", label: "transaction.card.fields.floodableZone", type: "checkbox" },
	{ key: "endangeredSpecies", label: "transaction.card.fields.endangeredSpecies", type: "checkbox" },
	{ key: "saleContext", label: "transaction.card.fields.saleContext", type: "select", options: TransactionSaleContext },
	{
		key: "currentState",
		label: "transaction.card.fields.currentState",
		type: "select",
		options: TransactionCurrentState,
	},
	{
		key: "codificationType",
		label: "transaction.card.fields.projectStatus",
		type: "select",
		options: TransactionCodificationType,
	},
	{
		key: "projectApproval",
		label: "transaction.card.fields.currentApproval",
		type: "select",
		options: TransactionProjectApproval,
	},
	{
		key: "projectApprovalAtDeedOfSale",
		label: "transaction.card.fields.approvalAtDeedOfSale",
		type: "select",
		options: TransactionProjectApprovalAtDeedOfSale,
	},
	{
		key: "contamination",
		label: "transaction.card.fields.contamination",
		type: "select",
		options: TransactionContamination,
	},
	{ key: "landShape", label: "transaction.card.fields.landShape", type: "select", options: TransactionLandShape },
	{
		key: "landSubtype",
		label: "comparableAnalysis.landSubtype",
		type: "select",
		options: [
			"Development - Gross Land",
			"Development - Serviced Land",
			"Conservation",
			"Agricultural",
			"Forestry",
			"Domain",
			"Special Assets",
			"Other",
		],
	},
	{ key: "buyerType", label: "transaction.card.fields.type", type: "select", options: TransactionPartyType },
	{ key: "sellerType", label: "transaction.card.fields.type", type: "select", options: TransactionPartyType },
	{ key: "cptaq", label: "transaction.card.fields.cptaq", type: "select", multiple: true, options: TransactionCPTAQ },
	{
		key: "urbanPerimeter",
		label: "transaction.card.fields.urbanPerimeter",
		type: "select",
		multiple: true,
		options: TransactionUrbanPerimeter,
	},
	{
		key: "infrastructure",
		label: "transaction.card.fields.infrastructure",
		type: "select",
		multiple: true,
		options: TransactionInfrastructure,
	},
	{
		key: "parkingTypes",
		label: "transaction.card.sections.parking",
		type: "select",
		multiple: true,
		options: Object.values(TransactionParkingType),
	},
	{ key: "deedOfSaleDate", label: "transaction.card.fields.deedOfSaleDate", type: "date" },
	{ key: "psaDate", label: "transaction.card.fields.psaDate", type: "date" },
	{
		key: "assetType",
		label: "transaction.card.fields.assetType",
		type: "select",
		multiple: true,
		options: Object.values(AssetType),
	},
	{
		key: "landCategoryNew",
		label: "transaction.card.fields.landCategory",
		type: "select",
		multiple: true,
		options: Object.values(LandCategory),
	},
	{
		key: "landSubcategory",
		label: "transaction.card.fields.landSubcategory",
		type: "select",
		multiple: true,
		options: Object.values(LandSubcategory),
	},
	{ key: "deductionFiles", label: "transaction.card.fields.deductionFiles", type: "text" },
]
